import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface ProductListing {
  id: string;
  user_id: string;
  title: string;
  description: string;
  price: number;
  category: string;
  condition: string;
  status: string;
  created_at: string;
  updated_at: string;
  images: string[];
  location: string;
  quantity?: number;
  subcategory?: string;
}

interface Offer {
  id: string;
  listing_id: string;
  buyer_id: string;
  seller_id: string;
  amount: number;
  status: string;
  created_at: string;
  message: string;
  listing_title?: string;
}

interface EscrowPayment {
  id: string;
  offer_id: string;
  buyer_id: string;
  seller_id: string;
  amount: number;
  status: string;
  created_at: string;
  updated_at: string;
  payment_method: string;
  transaction_id: string;
  listing_title?: string;
  payment_intent_id?: string;
  base_amount?: number;
  seller_released_amount?: number;
  buyer_fee?: number;
  seller_fee?: number;
  platform_fee?: number;
  stripe_fee?: number;
  platform_revenue?: number;
  transfer_id?: string;
  transfer_created_at?: string;
  payout_id?: string;
  payout_date?: string;
  payout_status?: string;
  released_at?: string;
  cancelled_at?: string;
  new_payment_intent_id?: string;
  listing_id?: string; // Added for easier filtering
  receipt_url?: string;
  currency_code?: string;
  risk_score?: number;
  risk_level?: string;
  authentication_method?: string;
  payment_method_type?: string;
  card_brand?: string;
  buyer_location_name?: string;
  buyer_latitude?: number;
  buyer_longitude?: number;
  seller_location_name?: string;
  seller_latitude?: number;
  seller_longitude?: number;
}

const UserProducts: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'listings' | 'orders'>('listings');
  const [listings, setListings] = useState<ProductListing[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [escrowPayments, setEscrowPayments] = useState<EscrowPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userDisplayName, setUserDisplayName] = useState<string>('');
  const [expandedListings, setExpandedListings] = useState<Set<string>>(new Set());
  
  // Search states for each tab
  const [listingsSearchTerm, setListingsSearchTerm] = useState('');
  const [ordersSearchTerm, setOrdersSearchTerm] = useState('');
  const [filteredListings, setFilteredListings] = useState<ProductListing[]>([]);
  const [filteredOffers, setFilteredOffers] = useState<Offer[]>([]);
  const [filteredEscrowPayments, setFilteredEscrowPayments] = useState<EscrowPayment[]>([]);

  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  // Filter listings based on search term
  useEffect(() => {
    if (listingsSearchTerm.trim() === '') {
      setFilteredListings(listings);
    } else {
      const filtered = listings.filter(listing => 
        (listing.title && typeof listing.title === 'string' && listing.title.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (listing.category && typeof listing.category === 'string' && listing.category.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (listing.subcategory && typeof listing.subcategory === 'string' && listing.subcategory.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (listing.location && typeof listing.location === 'string' && listing.location.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (listing.status && typeof listing.status === 'string' && listing.status.toLowerCase().includes(listingsSearchTerm.toLowerCase()))
      );
      setFilteredListings(filtered);
    }
  }, [listingsSearchTerm, listings]);

  // Filter orders based on search term
  useEffect(() => {
    if (ordersSearchTerm.trim() === '') {
      setFilteredOffers(offers);
      setFilteredEscrowPayments(escrowPayments);
    } else {
      const filteredOffers = offers.filter(offer => 
        (offer.listing_title && typeof offer.listing_title === 'string' && offer.listing_title.toLowerCase().includes(ordersSearchTerm.toLowerCase())) ||
        (offer.status && typeof offer.status === 'string' && offer.status.toLowerCase().includes(ordersSearchTerm.toLowerCase())) ||
        (offer.buyer_id && typeof offer.buyer_id === 'string' && offer.buyer_id.toLowerCase().includes(ordersSearchTerm.toLowerCase())) ||
        (offer.seller_id && typeof offer.seller_id === 'string' && offer.seller_id.toLowerCase().includes(ordersSearchTerm.toLowerCase()))
      );
      setFilteredOffers(filteredOffers);

      const filteredPayments = escrowPayments.filter(payment => 
        (payment.status && typeof payment.status === 'string' && payment.status.toLowerCase().includes(ordersSearchTerm.toLowerCase())) ||
        (payment.payment_method && typeof payment.payment_method === 'string' && payment.payment_method.toLowerCase().includes(ordersSearchTerm.toLowerCase())) ||
        (payment.buyer_id && typeof payment.buyer_id === 'string' && payment.buyer_id.toLowerCase().includes(ordersSearchTerm.toLowerCase())) ||
        (payment.seller_id && typeof payment.seller_id === 'string' && payment.seller_id.toLowerCase().includes(ordersSearchTerm.toLowerCase()))
      );
      setFilteredEscrowPayments(filteredPayments);
    }
  }, [ordersSearchTerm, offers, escrowPayments]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);



      // Get user email first
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      // Fetch user profile to get email and name
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('email, first_name, last_name, business_name, profile_type')
        .eq('id', userId)
        .single();

      if (profile?.email) {
        setUserEmail(profile.email);
      }

      // Set display name (business name for business profiles, first + last name for individuals)
      if (profile?.profile_type === 'business' && profile?.business_name) {
        setUserDisplayName(profile.business_name);
      } else if (profile?.first_name || profile?.last_name) {
        const firstName = profile?.first_name || '';
        const lastName = profile?.last_name || '';
        setUserDisplayName(`${firstName} ${lastName}`.trim());
      } else {
        setUserDisplayName(`User ID: ${userId}`);
      }

      // Fetch user's product listings
      const { data: listingsData, error: listingsError } = await supabase
        .from('ProductListings')
        .select('*')
        .eq('seller_id', userId)
        .order('created_at', { ascending: false });

      if (listingsError) {
        // Error fetching listings
      } else {
        setListings(listingsData || []);
      }

      // Fetch offers where user is buyer or seller
      const { data: offersData, error: offersError } = await supabase
        .from('offers')
        .select('*')
        .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
        .order('created_at', { ascending: false });

      if (offersError) {
        // Error fetching offers
      } else {
        
        // Get listing titles for offers
        if (offersData && offersData.length > 0) {
          const listingIds = offersData.map(offer => offer.listing_id);
          const { data: listingTitles, error: titlesError } = await supabase
            .from('ProductListings')
            .select('id, title')
            .in('id', listingIds);
          
          if (!titlesError && listingTitles) {
            const titleMap = listingTitles.reduce((acc, listing) => {
              acc[listing.id] = listing.title;
              return acc;
            }, {} as Record<string, string>);
            
            const processedOffers = offersData.map(offer => ({
              ...offer,
              listing_title: titleMap[offer.listing_id] || 'Unknown Listing'
            }));
            setOffers(processedOffers);
          } else {
            setOffers(offersData);
          }
        } else {
          setOffers([]);
        }
      }

      // Fetch escrow payments related to user's listings
      // First get all escrow payments, then filter by those related to user's listings
      const { data: escrowData, error: escrowError } = await supabase
        .from('escrow_payments')
        .select('*')
        .order('created_at', { ascending: false });
      

      
      // Also fetch offers to establish the relationship between escrow payments and listings
      
      // Try different possible table names
      let allOffersData = null;
      let allOffersError = null;
      
      // First try 'offers'
      const { data: offersData1, error: offersError1 } = await supabase
        .from('offers')
        .select('id, listing_id, buyer_id, seller_id')
        .order('created_at', { ascending: false });
      
      if (offersError1) {
        
        // Try 'Offers' (capitalized)
        const { data: offersData2, error: offersError2 } = await supabase
          .from('Offers')
          .select('id, listing_id, buyer_id, seller_id')
          .order('created_at', { ascending: false });
        
        if (offersError2) {
          
          // Try 'product_offers'
          const { data: offersData3, error: offersError3 } = await supabase
            .from('product_offers')
            .select('id, listing_id, buyer_id, seller_id')
            .order('created_at', { ascending: false });
          
          if (offersError3) {
            allOffersData = null;
            allOffersError = offersError3;
          } else {
            allOffersData = offersData3;
            allOffersError = null;
          }
        } else {
          allOffersData = offersData2;
          allOffersError = null;
        }
      } else {
        allOffersData = offersData1;
        allOffersError = null;
      }
      
      if (allOffersError) {
        // Error fetching offers
      }

      if (escrowError) {
        // Error fetching escrow payments
      } else {
        
        // For admin view, show all escrow payments
        // The accordion logic will filter them by listing
        if (escrowData && escrowData.length > 0) {
          // Create a mapping from offer_id to listing_id for quick lookup
          const offerToListingMap: Record<string, string> = {};
          if (allOffersData) {
            allOffersData.forEach(offer => {
              offerToListingMap[offer.id] = offer.listing_id;
            });
          }
          
          // Add listing_id to each escrow payment for easier filtering
          const processedEscrowPayments = escrowData.map(payment => {
            const listingId = offerToListingMap[payment.offer_id] || null;
            return {
              ...payment,
              listing_id: listingId
            };
          });
          
          setEscrowPayments(processedEscrowPayments);
        } else {
          setEscrowPayments([]);
        }
      }

    } catch (err) {
      setError('Error fetching user data');
    } finally {
      setLoading(false);
    }
  };

  const toggleListingExpansion = (listingId: string) => {
    setExpandedListings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listingId)) {
        newSet.delete(listingId);
      } else {
        newSet.add(listingId);
      }
      return newSet;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'completed':
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'pending':
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
      case 'rejected':
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBackToDashboard = () => {
    // Get dashboard state from location state or localStorage
    const dashboardState = location.state?.dashboardState || JSON.parse(localStorage.getItem('dashboardState') || '{}');
    
    if (dashboardState.activeSection) {
      // Navigate back to dashboard with preserved state
      navigate('/dashboard', { 
        state: { 
          preserveState: true,
          activeSection: dashboardState.activeSection,
          homeSearchTerm: dashboardState.homeSearchTerm,
          filteredUsers: dashboardState.filteredUsers,
          userManagementSearchTerm: dashboardState.userManagementSearchTerm,
          currentPage: dashboardState.currentPage
        }
      });
    } else {
      // Fallback to simple back navigation
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#00a67e] shadow-sm border-b border-[#008f6b]">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">User Products</h1>
              <p className="text-green-100 mt-1">
                {userDisplayName}
              </p>
              <p className="text-green-200 text-sm mt-1">
                {userEmail || `User ID: ${userId}`}
              </p>
            </div>
            <button
              onClick={handleBackToDashboard}
              className="px-4 py-2 bg-[#008f6b] text-white rounded-lg hover:bg-[#007a5a] transition-colors duration-200"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('listings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'listings'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              User Listings ({filteredListings.length}/{listings.length})
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'orders'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              User Orders ({(filteredOffers.filter(offer => offer.buyer_id === userId).length + filteredEscrowPayments.filter(payment => payment.buyer_id === userId).length)}/{offers.length + escrowPayments.length})
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'listings' ? (
          /* User Listings Tab */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Product Listings</h2>
              <p className="text-sm text-gray-600 mt-1">
                Products listed by this user
              </p>
              
              {/* Search Bar */}
              <div className="mt-4">
                <div className="relative max-w-md">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search listings by title, category, location, or status..."
                    value={listingsSearchTerm}
                    onChange={(e) => setListingsSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                {listingsSearchTerm && (
                  <div className="mt-2 text-sm text-gray-600">
                    Showing {filteredListings.length} of {listings.length} listings
                  </div>
                )}
              </div>
            </div>
            
            {filteredListings.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                {listingsSearchTerm ? (
                  <>
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No listings found</h3>
                    <p className="mt-1 text-sm text-gray-500">No listings match your search criteria "{listingsSearchTerm}".</p>
                  </>
                ) : (
                  <>
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No listings found</h3>
                    <p className="mt-1 text-sm text-gray-500">This user hasn't listed any products yet.</p>
                  </>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredListings.map((listing) => (
                      <React.Fragment key={listing.id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{listing.title}</div>
                                <div className="text-sm text-gray-500">{listing.location || 'No location'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatPrice(listing.price)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>
                              <div>{listing.category || 'Uncategorized'}</div>
                              {listing.subcategory && (
                                <div className="text-xs text-gray-400">{listing.subcategory}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {listing.quantity || 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(listing.status)}`}>
                              {listing.status || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(listing.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => toggleListingExpansion(listing.id)}
                              className="flex items-center justify-center cursor-pointer p-2 bg-[#e6f7f0] rounded-lg hover:bg-[#ccf0e0] transition-colors duration-200"
                            >
                              <svg 
                                className={`w-4 h-4 text-[#00a67e] transition-transform duration-200 ${expandedListings.has(listing.id) ? 'rotate-180' : ''}`} 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                        
                        {/* Listing Details Row (expanded) */}
                        {expandedListings.has(listing.id) && (
                          <tr>
                            <td colSpan={7} className="px-6 py-4 bg-[#e6f7f0]">
                              <div className="space-y-4">
                                <h4 className="text-sm font-medium text-[#00a67e] mb-3">Payment Details</h4>
                                
                                {/* Escrow Payment Details */}
                                <div className="bg-white rounded-lg p-3 border border-indigo-200">
                                  <h5 className="text-xs font-medium text-indigo-600 uppercase tracking-wider mb-2">Payment Details</h5>
                                  
                                  {/* Find related escrow payments for this listing */}
                                  {(() => {
                                    // Find escrow payments that are related to this specific listing
                                    const relatedEscrowPayments = escrowPayments.filter(payment => {
                                      // Now that we've added listing_id to each escrow payment,
                                      // we can directly check if it matches this listing
                                      const isRelated = payment.listing_id === listing.id;
                                      return isRelated;
                                    });
                                    
                                    if (relatedEscrowPayments.length > 0) {
                                      // Show escrow payment data
                                      const payment = relatedEscrowPayments[0]; // Show first payment
                                      return (
                                        <>
                                          {/* Payment Intent ID and Status */}
                                          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center justify-between mb-2">
                                              <div className="text-sm font-medium text-gray-900">
                                                Payment Intent ID: {payment.payment_intent_id || 'N/A'}
                                              </div>
                                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                                                {payment.status || 'Unknown'}
                                              </span>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 text-sm">
                                              <div>
                                                <span className="text-gray-600">Listing ID: </span>
                                                <span className="font-medium text-xs">{listing.id}</span>
                                              </div>
                                              <div>
                                                <span className="text-gray-600">Buyer ID: </span>
                                                <span className="font-medium text-xs">{payment.buyer_id || 'N/A'}</span>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                            {/* Amount Details */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Amount Details</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Total Amount:</span>
                                                  <span className="font-medium">{formatPrice(payment.amount)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Base Amount:</span>
                                                  <span className="font-medium">{formatPrice(payment.base_amount || 0)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Seller Released:</span>
                                                  <span className="font-medium">{formatPrice(payment.seller_released_amount || 0)}</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Fee Breakdown */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Fee Breakdown</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Buyer Fee:</span>
                                                  <span className="font-medium">{formatPrice(payment.buyer_fee || 0)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Seller Fee:</span>
                                                  <span className="font-medium">{formatPrice(payment.seller_fee || 0)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Platform Fee:</span>
                                                  <span className="font-medium">{formatPrice(payment.platform_fee || 0)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Stripe Fee:</span>
                                                  <span className="font-medium">{formatPrice(payment.stripe_fee || 0)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Platform Revenue:</span>
                                                  <span className="font-medium">{formatPrice(payment.platform_revenue || 0)}</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Transfer Information */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Transfer Information</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Transfer ID:</span>
                                                  <span className="font-medium text-xs">{payment.transfer_id || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Transfer Created:</span>
                                                  <span className="font-medium text-xs">{payment.transfer_created_at ? formatDate(payment.transfer_created_at) : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payout ID:</span>
                                                  <span className="font-medium text-xs">{payment.payout_id || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payout Date:</span>
                                                  <span className="font-medium text-xs">{payment.payout_date ? formatDate(payment.payout_date) : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payout Status:</span>
                                                  <span className="font-medium text-xs">{payment.payout_status || 'N/A'}</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Additional Details */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Additional Details</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payment Method:</span>
                                                  <span className="font-medium text-xs">{payment.payment_method || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Released At:</span>
                                                  <span className="font-medium text-xs">{payment.released_at ? formatDate(payment.released_at) : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Cancelled At:</span>
                                                  <span className="font-medium text-xs">{payment.cancelled_at ? formatDate(payment.cancelled_at) : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">New Payment Intent:</span>
                                                  <span className="font-medium text-xs">{payment.new_payment_intent_id || 'N/A'}</span>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </>
                                      );
                                    } else {
                                      return (
                                        <div className="text-center py-4 text-gray-500">
                                          <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-2 2v8a3 3 0 002 3z" />
                                          </svg>
                                          <p className="text-sm">No escrow payments found for this listing</p>
                                        </div>
                                      );
                                    }
                                  })()}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* User Orders Tab */
          <div className="space-y-6">
            {/* Offers with Escrow Payments Accordion */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">User Orders (Buyer)</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Offers and payment transactions where this user is the buyer
                </p>
                
                {/* Search Bar */}
                <div className="mt-4">
                  <div className="relative max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search orders by listing title, status, or user ID..."
                      value={ordersSearchTerm}
                      onChange={(e) => setOrdersSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  {ordersSearchTerm && (
                    <div className="mt-2 text-sm text-gray-600">
                      Showing {filteredOffers.filter(offer => offer.buyer_id === userId).length + filteredEscrowPayments.filter(payment => payment.buyer_id === userId).length} of {offers.length + escrowPayments.length} orders
                    </div>
                  )}
                </div>
              </div>
              
              {filteredOffers.filter(offer => offer.buyer_id === userId).length === 0 && filteredEscrowPayments.filter(payment => payment.buyer_id === userId).length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No offers or payments found</h3>
                  <p className="mt-1 text-sm text-gray-500">This user hasn't been involved in any offers or payment transactions yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Listing/Offer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Render offers where user is buyer */}
                      {filteredOffers.filter(offer => offer.buyer_id === userId).map((offer) => (
                        <React.Fragment key={`offer-${offer.id}`}>
                          <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            <div>
                                <div className="text-sm font-medium text-gray-900">{offer.listing_title}</div>
                                <div className="text-sm text-gray-500">Seller ID: {offer.seller_id}</div>
                              </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatPrice(offer.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {offer.buyer_id === userId ? 'Buyer' : 'Seller'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(offer.status)}`}>
                              {offer.status || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(offer.created_at)}
                          </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => toggleListingExpansion(`offer-${offer.id}`)}
                                className="flex items-center justify-center cursor-pointer p-2 bg-[#e6f7f0] rounded-lg hover:bg-[#ccf0e0] transition-colors duration-200"
                              >
                                <svg 
                                  className={`w-4 h-4 text-[#00a67e] transition-transform duration-200 ${expandedListings.has(`offer-${offer.id}`) ? 'rotate-180' : ''}`} 
                                  fill="none" 
                                  viewBox="0 0 24 24" 
                                  stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                          </td>
                        </tr>
                          
                          {/* Offer Details Row (expanded) */}
                          {expandedListings.has(`offer-${offer.id}`) && (
                            <tr>
                                                                                     <td colSpan={6} className="px-6 py-4 bg-[#e6f7f0]">
                               <div className="space-y-4">
                                 <h4 className="text-sm font-medium text-[#00a67e] mb-3">Offer Details</h4>
                                  
                                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <span className="text-gray-600">Offer ID: </span>
                                        <span className="font-medium text-xs">{offer.id}</span>
                </div>
                                      <div>
                                        <span className="text-gray-600">Listing ID: </span>
                                        <span className="font-medium text-xs">{offer.listing_id}</span>
            </div>

                                      

                                    </div>
              </div>
              
                                  {/* Show related escrow payment if exists */}
                                  {(() => {
                                    const relatedEscrowPayment = escrowPayments.find(payment => 
                                      payment.offer_id === offer.id
                                    );
                                    
                                    if (relatedEscrowPayment) {
                                      return (
                                                                                                                                                                   <div className="bg-white rounded-lg p-3 border border-[#00a67e]">
                                            <h5 className="text-xs font-medium text-[#00a67e] uppercase tracking-wider mb-2">Payment Details</h5>
                                          
                                          {/* Payment Intent ID and Status */}
                                          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center justify-between mb-2">
                                              <div className="text-sm font-medium text-gray-900">
                                                Payment Intent ID: {relatedEscrowPayment.payment_intent_id || 'N/A'}
                                              </div>
                                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(relatedEscrowPayment.status)}`}>
                                                {relatedEscrowPayment.status || 'Unknown'}
                                              </span>
                                            </div>
                                          </div>
                                          
                                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                            {/* Amount Details */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Amount Details</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Total Amount:</span>
                                                  <span className="font-medium">{formatPrice(relatedEscrowPayment.amount)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Base Amount:</span>
                                                  <span className="font-medium">{formatPrice(relatedEscrowPayment.base_amount || 0)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Seller Released:</span>
                                                  <span className="font-medium">{formatPrice(relatedEscrowPayment.seller_released_amount || 0)}</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Fee Breakdown */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Fee Breakdown</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Buyer Fee:</span>
                                                  <span className="font-medium">{formatPrice(relatedEscrowPayment.buyer_fee || 0)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Seller Fee:</span>
                                                  <span className="font-medium">{formatPrice(relatedEscrowPayment.seller_fee || 0)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Platform Fee:</span>
                                                  <span className="font-medium">{formatPrice(relatedEscrowPayment.platform_fee || 0)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Stripe Fee:</span>
                                                  <span className="font-medium">{formatPrice(relatedEscrowPayment.stripe_fee || 0)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Platform Revenue:</span>
                                                  <span className="font-medium">{formatPrice(relatedEscrowPayment.platform_revenue || 0)}</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Transfer Information */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Transfer Information</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Transfer ID:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.transfer_id || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Transfer Created:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.transfer_created_at ? formatDate(relatedEscrowPayment.transfer_created_at) : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payout ID:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.payout_id || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payout Date:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.payout_date ? formatDate(relatedEscrowPayment.payout_date) : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payout Status:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.payout_status || 'N/A'}</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Additional Details */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Additional Details</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payment Method:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.payment_method || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Released At:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.released_at ? formatDate(relatedEscrowPayment.released_at) : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Cancelled At:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.cancelled_at ? formatDate(relatedEscrowPayment.cancelled_at) : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">New Payment Intent:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.new_payment_intent_id || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Currency Code:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.currency_code || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Receipt URL:</span>
                                                  {relatedEscrowPayment.receipt_url ? (
                                                    <a href={relatedEscrowPayment.receipt_url} target="_blank" rel="noopener noreferrer" className="font-medium text-xs text-blue-600 hover:underline">
                                                      View Receipt
                                                    </a>
                                                  ) : (
                                                    <span className="font-medium text-xs">N/A</span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          {/* Risk & Security Details */}
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Risk & Security</h6>
                                              <div className="space-y-1">
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Risk Score:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.risk_score !== undefined ? relatedEscrowPayment.risk_score : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Risk Level:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.risk_level || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Authentication Method:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.authentication_method || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Payment Method Type:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.payment_method_type || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                  <span className="text-gray-600">Card Brand:</span>
                                                  <span className="font-medium text-xs">{relatedEscrowPayment.card_brand || 'N/A'}</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Location Details */}
                                            <div className="bg-gray-50 rounded-lg p-3">
                                              <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Location Details</h6>
                                              <div className="space-y-2">
                                                <div>
                                                  <div className="text-xs font-medium text-gray-700 mb-1">Buyer Location:</div>
                                                  <div className="text-xs text-gray-600">{relatedEscrowPayment.buyer_location_name || 'N/A'}</div>
                                                  {relatedEscrowPayment.buyer_latitude && relatedEscrowPayment.buyer_longitude && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                      Lat: {relatedEscrowPayment.buyer_latitude}, Long: {relatedEscrowPayment.buyer_longitude}
                                                    </div>
                                                  )}
                                                </div>
                                                <div>
                                                  <div className="text-xs font-medium text-gray-700 mb-1">Seller Location:</div>
                                                  <div className="text-xs text-gray-600">{relatedEscrowPayment.seller_location_name || 'N/A'}</div>
                                                  {relatedEscrowPayment.seller_latitude && relatedEscrowPayment.seller_longitude && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                      Lat: {relatedEscrowPayment.seller_latitude}, Long: {relatedEscrowPayment.seller_longitude}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div className="text-center py-4 text-gray-500">
                                          <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-2 2v8a3 3 0 002 3z" />
                  </svg>
                                          <p className="text-sm">No escrow payment found for this offer</p>
                </div>
                                      );
                                    }
                                  })()}
                                </div>
                              </td>
                      </tr>
                          )}
                        </React.Fragment>
                      ))}
                      
                      {/* Render escrow payments where user is buyer and don't have associated offers */}
                      {filteredEscrowPayments
                        .filter(payment => payment.buyer_id === userId && !offers.some(offer => offer.id === payment.offer_id))
                        .map((payment) => (
                        <React.Fragment key={`payment-${payment.id}`}>
                          <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-2">
                                  Payment
                                </span>
                                {payment.listing_title || 'Direct Payment'}
                              </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatPrice(payment.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {payment.buyer_id === userId ? 'Buyer' : 'Seller'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                              {payment.status || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(payment.created_at)}
                          </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => toggleListingExpansion(`payment-${payment.id}`)}
                                className="flex items-center justify-center cursor-pointer p-2 bg-green-50 rounded-lg hover:bg-green-100 transition-colors duration-200"
                              >
                                <svg 
                                  className={`w-4 h-4 text-green-600 transition-transform duration-200 ${expandedListings.has(`payment-${payment.id}`) ? 'rotate-180' : ''}`} 
                                  fill="none" 
                                  viewBox="0 0 24 24" 
                                  stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                          </td>
                        </tr>
                          
                          {/* Payment Details Row (expanded) */}
                          {expandedListings.has(`payment-${payment.id}`) && (
                            <tr>
                              <td colSpan={6} className="px-6 py-4 bg-green-50">
                                <div className="space-y-4">
                                  <h4 className="text-sm font-medium text-green-700 mb-3">Payment Details</h4>
                                  
                                  <div className="bg-white rounded-lg p-3 border border-green-200">
                                    {/* Payment Intent ID and Status */}
                                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="text-sm font-medium text-gray-900">
                                          Payment Intent ID: {payment.payment_intent_id || 'N/A'}
                                        </div>
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                                          {payment.status || 'Unknown'}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 text-sm">
                                        <div>
                                          <span className="text-gray-600">Payment ID: </span>
                                          <span className="font-medium text-xs">{payment.id}</span>
                                        </div>
                                        <div>
                                          <span className="text-gray-600">Offer ID: </span>
                                          <span className="font-medium text-xs">{payment.offer_id || 'N/A'}</span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                      {/* Amount Details */}
                                      <div className="bg-gray-50 rounded-lg p-3">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Amount Details</h6>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Total Amount:</span>
                                            <span className="font-medium">{formatPrice(payment.amount)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Base Amount:</span>
                                            <span className="font-medium">{formatPrice(payment.base_amount || 0)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Seller Released:</span>
                                            <span className="font-medium">{formatPrice(payment.seller_released_amount || 0)}</span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Fee Breakdown */}
                                      <div className="bg-gray-50 rounded-lg p-3">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Fee Breakdown</h6>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Buyer Fee:</span>
                                            <span className="font-medium">{formatPrice(payment.buyer_fee || 0)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Seller Fee:</span>
                                            <span className="font-medium">{formatPrice(payment.seller_fee || 0)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Platform Fee:</span>
                                            <span className="font-medium">{formatPrice(payment.platform_fee || 0)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Stripe Fee:</span>
                                            <span className="font-medium">{formatPrice(payment.stripe_fee || 0)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Platform Revenue:</span>
                                            <span className="font-medium">{formatPrice(payment.platform_revenue || 0)}</span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Transfer Information */}
                                      <div className="bg-gray-50 rounded-lg p-3">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Transfer Information</h6>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Transfer ID:</span>
                                            <span className="font-medium text-xs">{payment.transfer_id || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Transfer Created:</span>
                                            <span className="font-medium text-xs">{payment.transfer_created_at ? formatDate(payment.transfer_created_at) : 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Payout ID:</span>
                                            <span className="font-medium text-xs">{payment.payout_id || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Payout Date:</span>
                                            <span className="font-medium text-xs">{payment.payout_date ? formatDate(payment.payout_date) : 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Payout Status:</span>
                                            <span className="font-medium text-xs">{payment.payout_status || 'N/A'}</span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Additional Details */}
                                      <div className="bg-gray-50 rounded-lg p-3">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Additional Details</h6>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Payment Method:</span>
                                            <span className="font-medium text-xs">{payment.payment_method || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Released At:</span>
                                            <span className="font-medium text-xs">{payment.released_at ? formatDate(payment.released_at) : 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Cancelled At:</span>
                                            <span className="font-medium text-xs">{payment.cancelled_at ? formatDate(payment.cancelled_at) : 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">New Payment Intent:</span>
                                            <span className="font-medium text-xs">{payment.new_payment_intent_id || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Currency Code:</span>
                                            <span className="font-medium text-xs">{payment.currency_code || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Receipt URL:</span>
                                            {payment.receipt_url ? (
                                              <a href={payment.receipt_url} target="_blank" rel="noopener noreferrer" className="font-medium text-xs text-blue-600 hover:underline">
                                                View Receipt
                                              </a>
                                            ) : (
                                              <span className="font-medium text-xs">N/A</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Risk & Security Details */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                                      <div className="bg-gray-50 rounded-lg p-3">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Risk & Security</h6>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Risk Score:</span>
                                            <span className="font-medium text-xs">{payment.risk_score !== undefined ? payment.risk_score : 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Risk Level:</span>
                                            <span className="font-medium text-xs">{payment.risk_level || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Authentication Method:</span>
                                            <span className="font-medium text-xs">{payment.authentication_method || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Payment Method Type:</span>
                                            <span className="font-medium text-xs">{payment.payment_method_type || 'N/A'}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Card Brand:</span>
                                            <span className="font-medium text-xs">{payment.card_brand || 'N/A'}</span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Location Details */}
                                      <div className="bg-gray-50 rounded-lg p-3">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Location Details</h6>
                                        <div className="space-y-2">
                                          <div>
                                            <div className="text-xs font-medium text-gray-700 mb-1">Buyer Location:</div>
                                            <div className="text-xs text-gray-600">{payment.buyer_location_name || 'N/A'}</div>
                                            {payment.buyer_latitude && payment.buyer_longitude && (
                                              <div className="text-xs text-gray-500 mt-1">
                                                Lat: {payment.buyer_latitude}, Long: {payment.buyer_longitude}
                                              </div>
                                            )}
                                          </div>
                                          <div>
                                            <div className="text-xs font-medium text-gray-700 mb-1">Seller Location:</div>
                                            <div className="text-xs text-gray-600">{payment.seller_location_name || 'N/A'}</div>
                                            {payment.seller_latitude && payment.seller_longitude && (
                                              <div className="text-xs text-gray-500 mt-1">
                                                Lat: {payment.seller_latitude}, Long: {payment.seller_longitude}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProducts;
